<script async setup>
import { ref, defineProps, watch, computed, defineEmits, onMounted, onBeforeUnmount, nextTick } from "vue";
import KdFormElement from "./KdFormElement.vue";

const props = defineProps({
    name: {
        type: String,
        default: "",
    },
    label: {
        type: String,
        default: "",
    },
    error: {
        type: String,
        default: "",
    },
    placeholder: {
        type: String,
        default: "",
    },
    required: {
        type: Boolean,
        default: false,
    },

    multiple: {
        type: Boolean,
        default: false,
    },

    dropdown: {
        type: Boolean,
        default: false,
    },

    value: {
        type: [Object, Array, String, Number],
        default: "",
    },

    disabled: {
        type: Boolean,
        default: false,
    },

    options: {
        type: Array,
        required: true,
    },

    icon: {
        type: String,
        default: "",
    },
    iconClass: {
        type: String,
        default: "",
    },

    variant: {
        type: String,
        default: "dropdown", // "dropdown" | "filter"
    },

    slider: {
        type: Boolean,
        default: false,
    },
    sliderMin: {
        type: Number,
        default: 0,
    },
    sliderMax: {
        type: Number,
        default: 10000,
    },
    sliderStep: {
        type: Number,
        default: 1,
    },
    sliderStart: {
        type: Number,
        default: 0,
    },

    dataTax: {
        type: String,
        default: "",
    },
    checkboxes: {
        type: Boolean,
        default: false,
    },
    currency: {
        type: String,
        default: "€",
    },
});

const emit = defineEmits(["update:value"]);


/* SLIDER */

const rangeValue = ref({
    min_price: props.value?.min_price ?? props.sliderMin,
    max_price: props.value?.max_price ?? props.sliderMax,
});

const isDefaultRange = computed(() => {
    return (
        rangeValue.value.min_price === props.sliderMin &&
        rangeValue.value.max_price === props.sliderMax
    );
});

const rangeLabel = computed(() => {
    if (!props.slider) return props.placeholder || props.label;

    if (isDefaultRange.value) {
        return props.label || props.placeholder;
    }

    return `${rangeValue.value.min_price}${props.currency} – ${rangeValue.value.max_price}${props.currency}`;
});

let priceDebounce = null;

watch(
    rangeValue,
    (value) => {
        if (!props.slider) return;

        emit("update:value", {
            min_price: value.min_price,
            max_price: value.max_price,
        });

        if (priceDebounce) {
            clearTimeout(priceDebounce);
        }

        priceDebounce = setTimeout(() => {
            dispatchFiltersChanged();
        }, 400);
    },
    { deep: true }
);


/* REST */

const toArray = (v) => (v == null || v === "" ? [] : Array.isArray(v) ? v : [v]);

const isMulti = computed(() => props.multiple);
const isFilter = computed(() => props.variant === "filter");
const useCheckboxList = computed(() => isFilter.value && (props.multiple || props.checkboxes));

const isOpen = ref(false);
const rootEl = ref(null);

const localValue = ref(
    useCheckboxList.value
        ? toArray(props.value)
        : props.multiple
            ? toArray(props.value)
            : props.value ?? ""
);

watch(
    () => props.value,
    (v) => {
        if (props.slider) {
            rangeValue.value = {
                min_price: v?.min_price ?? props.sliderMin,
                max_price: v?.max_price ?? props.sliderMax,
            };
            return;
        }

        if (useCheckboxList.value) {
            localValue.value = toArray(v);
            return;
        }

        localValue.value = isMulti.value ? toArray(v) : v ?? "";
    },
    { deep: true }
);

const handleChange = async () => {
    const outgoingValue =
        useCheckboxList.value && !props.multiple
            ? (localValue.value[0] || "")
            : localValue.value;

    emit("update:value", outgoingValue);

    await nextTick();
    dispatchFiltersChanged();
};

const onCheckboxChange = (checked, optionValue) => {
    if (!useCheckboxList.value) return;

    const isChecked = typeof checked === "boolean"
        ? checked
        : !!checked?.target?.checked;

    if (props.multiple) {
        const arr = [...localValue.value];

        if (isChecked) {
            if (!arr.includes(optionValue)) arr.push(optionValue);
        } else {
            localValue.value = arr.filter((v) => v !== optionValue);
            handleChange();
            return;
        }

        localValue.value = arr;
        handleChange();
        return;
    }

    localValue.value = isChecked ? [optionValue] : [];
    handleChange();
};

const showFloatingLabel = computed(() => !props.slider && !isFilter.value);

const selectedSingleValue = computed(() => {
    return Array.isArray(localValue.value) ? (localValue.value[0] || "") : (localValue.value || "");
});

const selectedSingleOption = computed(() => {
    const val = selectedSingleValue.value;
    return props.options.find((opt) => String(opt.value) === String(val)) || null;
});

const selectSingleOption = (value) => {
    localValue.value = value;
    isOpen.value = false;
    handleChange();
};

const selectedCount = computed(() => toArray(localValue.value).length);

const onDocumentClick = (event) => {
    if (!rootEl.value) return;
    if (rootEl.value.contains(event.target)) return;
    isOpen.value = false;
};

const dispatchFiltersChanged = () => {
    document.dispatchEvent(new CustomEvent("kd:filters-changed"));
};

onMounted(() => {
    document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", onDocumentClick);
});
</script>

<template>
    <kd-form-element
        :label="showFloatingLabel ? props.label : null"
        :error="props.error"
        :required="props.required"
    >
        <div ref="rootEl">
            <!-- SLIDER --> 
            <div v-if="props.slider" class="relative">
                <input type="hidden" name="min_price" :value="isDefaultRange ? '' : rangeValue.min_price">
                <input type="hidden" name="max_price" :value="isDefaultRange ? '' : rangeValue.max_price">
                <button
                    type="button"
                    class="flex w-full items-center justify-between border border-ts-black bg-white px-3 py-1 text-sm"
                    :class="isOpen ? 'rounded-t-2xl' : 'rounded-2xl'"
                    @click="isOpen = !isOpen"
                >
                    <span class="text-sm">{{ rangeLabel }}</span>

                    <kd-icon
                        v-if="icon"
                        :icon="icon"
                        class="absolute right-2 top-1/2 -translate-y-1/2 z-30 pointer-events-none"
                        :class="[iconClass, isOpen ? 'rotate-180' : 'rotate-0']"
                        size="15"
                        fill="black"
                    />
                </button>
                <div
                    v-show="isOpen"
                    class="absolute inset-x-0 mt-0 border-x border-b border-ts-black rounded-b-xl bg-white shadow-lg max-h-44 px-4 pt-2 pb-6 overflow-auto z-10"
                >
                    <kd-range-slider
                        v-model="rangeValue"
                        :min="props.sliderMin"
                        :max="props.sliderMax"
                        :step="props.sliderStep"
                        :currency="props.currency"
                    />
                </div>
            </div>

            <!-- FILTER SINGLE -->
            <div v-else-if="variant === 'filter' && !props.multiple && !props.checkboxes" class="relative">
                <input
                    v-if="props.name"
                    type="hidden"
                    :name="props.name"
                    :value="selectedSingleValue || ''"
                >

                <button
                    type="button"
                    class="flex w-full items-center justify-between border border-ts-black bg-white px-3 py-1 text-sm"
                    :class="isOpen ? 'rounded-t-2xl' : 'rounded-2xl'"
                    @click="isOpen = !isOpen"
                >
                    <span class="text-sm">
                        {{
                            selectedSingleOption ? selectedSingleOption.label : (label || placeholder)
                        }}
                    </span>

                    <kd-icon
                        v-if="icon"
                        :icon="icon"
                        class="transition-transform duration-200"
                        :class="[iconClass, isOpen ? 'rotate-180' : 'rotate-0']"
                        size="15"
                        fill="black"
                    />
                </button>

                <div
                    v-show="isOpen"
                    class="absolute inset-x-0 mt-0 border-x border-b border-ts-black rounded-b-xl bg-white shadow-lg max-h-42 overflow-auto z-10"
                >
                    <button
                        v-for="(item, index) in options"
                        :key="index"
                        type="button"
                        class="w-full text-left px-3 py-1 text-sm hover:bg-ts-light"
                        @click="selectSingleOption(item.value)"
                    >
                        {{ item.label }}
                    </button>
                </div>
            </div>

            <!-- FILTER CHECKBOX LIST -->
            <div v-else-if="variant === 'filter' && (props.multiple || props.checkboxes)" class="relative">
                <button
                    type="button"
                    class="flex w-full items-center justify-between border border-ts-black bg-white px-3 py-1 text-sm"
                    :class="isOpen ? 'rounded-t-2xl' : 'rounded-2xl'"
                    @click="isOpen = !isOpen"
                >
                    <div class="flex gap-1">
                        <span>{{ label }}</span>
                        <span v-if="selectedCount" class="text-sm">
                            ({{ selectedCount }})
                        </span>
                    </div>

                    <kd-icon
                        v-if="icon"
                        :icon="icon"
                        class="transition-transform duration-200"
                        :class="[iconClass, isOpen ? 'rotate-180' : 'rotate-0']"
                        size="15"
                        fill="black"
                    />
                </button>

                <div
                    v-show="isOpen"
                    class="absolute inset-x-0 mt-0 border-x border-b border-ts-black rounded-b-xl bg-white shadow-lg max-h-42 overflow-auto z-10"
                >
                    <kd-checkbox
                        v-for="(item, index) in options"
                        :key="index"
                        :name="props.name ? (props.multiple ? props.name + '[]' : props.name) : null"
                        :data-tax="props.dataTax || props.name"
                        :value="item.value"
                        :selected="localValue.includes(item.value)"
                        @update:selected="(val) => onCheckboxChange(val, item.value)"
                    >
                        <span class="flex items-start gap-0.5 phone:gap-2 min-w-0">
                            <span
                                v-if="(props.dataTax || props.name) === 'pa_farbe' && item.hex"
                                class="inline-flex size-4 rounded-full border border-gray-300 p-0.5 shrink-0"
                            >
                                <span
                                    class="block size-full rounded-full"
                                    :style="{
                                        background: item.hex2
                                            ? `linear-gradient(135deg, ${item.hex} 50%, ${item.hex2} 50%)`
                                            : item.hex
                                    }"
                                ></span>
                            </span>

                            <span>{{ item.label }}</span>
                        </span>
                    </kd-checkbox>
                </div>
            </div>

            <!-- NATIVE SELECT -->
            <div v-else-if="dropdown" class="block relative">
                <select
                    v-model="localValue"
                    :name="props.name || null"
                    :multiple="isMulti"
                    :disabled="props.disabled"
                    class="w-full rounded-xl border border-ts-black px-3 py-1 text-sm bg-white"
                    @change="handleChange"
                >
                    <option value="" v-if="props.placeholder">
                        {{ props.placeholder }}
                    </option>
                    <option
                        v-for="(item, index) in props.options"
                        :key="index"
                        :value="item.value"
                    >
                        {{ item.label }}
                    </option>
                </select>

                <kd-icon
                    v-if="props.icon"
                    :icon="props.icon"
                    class="absolute right-2 top-1/2 -translate-y-1/2 z-30 pointer-events-none"
                    :class="[props.iconClass, isOpen ? 'rotate-180' : 'rotate-0']"
                    size="15"
                    fill="black"
                />
            </div>
        </div>
    </kd-form-element>
</template>